import api from "../utils/api";

/**
 * @typedef {Object} Payment
 * @property {string} _id
 * @property {string} user
 * @property {number} amount
 * @property {string} currency
 * @property {string} status - 'pending', 'completed', 'failed', 'refunded'
 * @property {string} paymentMethod
 * @property {string} transactionId
 * @property {string} itemType - 'course', 'subscription'
 * @property {string} itemId
 * @property {string} receiptUrl
 * @property {string} createdAt
 */

/**
 * @typedef {Object} CreatePaymentData
 * @property {number} amount
 * @property {string} currency
 * @property {string} paymentMethod
 * @property {string} [transactionId]
 * @property {string} itemType
 * @property {string} itemId
 */

export const paymentService = {
  // ==================
  // PAYMENT ROUTES
  // ==================

  /**
   * Create payment record
   * @param {CreatePaymentData} paymentData
   * @returns {Promise<{status: string, payment: Payment}>}
   */
  createPayment: async (paymentData) => {
    const response = await api.post("/api/payments", paymentData);
    return response;
  },

  /**
   * Get payments by user
   * @param {string} userId
   * @returns {Promise<{status: string, count: number, payments: Payment[]}>}
   */
  getPaymentsByUser: async (userId) => {
    const response = await api.get(`/api/payments/user/${userId}`);
    return response;
  },

  /**
   * Get all payments (Admin)
   * @returns {Promise<{status: string, count: number, totalAmount: number, payments: Payment[]}>}
   */
  getAllPayments: async () => {
    const response = await api.get("/api/payments");
    return response;
  },

  /**
   * Delete payment (Admin)
   * @param {string} paymentId
   * @returns {Promise<{status: string, message: string}>}
   */
  deletePayment: async (paymentId) => {
    const response = await api.delete(`/api/payments/${paymentId}`);
    return response;
  },

  // ==================
  // ADMIN PAYMENT ROUTES
  // ==================

  /**
   * Get all payments with pagination and filters (Admin)
   * @param {Object} params - Query params (page, limit, status, paymentMethod)
   * @returns {Promise<{status: string, results: number, totalPages: number, currentPage: number, data: Payment[]}>}
   */
  getAdminPayments: async (params = {}) => {
    const response = await api.get("/api/admin/payments", { params });
    return response;
  },

  /**
   * Get payment statistics (Admin)
   * @returns {Promise<{status: string, data: {byStatus: Array, byPaymentMethod: Array, totalRevenue: number}}>}
   */
  getPaymentStats: async () => {
    const response = await api.get("/api/admin/payments/stats");
    return response;
  },

  /**
   * Get payments by date range (Admin)
   * @param {string} startDate
   * @param {string} endDate
   * @returns {Promise<{status: string, results: number, totalAmount: number, data: Payment[]}>}
   */
  getPaymentsByDateRange: async (startDate, endDate) => {
    const response = await api.get("/api/admin/payments/date-range", {
      params: { startDate, endDate },
    });
    return response;
  },

  /**
   * Update payment status (Admin)
   * @param {string} paymentId
   * @param {string} status - 'pending', 'completed', 'failed'
   * @returns {Promise<{status: string, message: string, data: Payment}>}
   */
  updatePaymentStatus: async (paymentId, status) => {
    const response = await api.patch(
      `/api/admin/payments/${paymentId}/status`,
      { status }
    );
    return response;
  },

  /**
   * Delete payment (Admin)
   * @param {string} paymentId
   * @returns {Promise<{status: string, message: string}>}
   */
  deleteAdminPayment: async (paymentId) => {
    const response = await api.delete(`/api/admin/payments/${paymentId}`);
    return response;
  },

  /**
   * Create manual payment (Admin)
   * @param {Object} paymentData - { user, subscription, amount, transactionId, currency, paymentMethod, status }
   * @returns {Promise<{status: string, message: string, data: Payment}>}
   */
  createAdminPayment: async (paymentData) => {
    const response = await api.post("/api/admin/payments", paymentData);
    return response;
  },

  /**
   * Refund a PayTabs-paid order. The backend calls PayTabs first; only on
   * success does it mutate the local order and payment records.
   */
  refundPayTabsPayment: async ({ orderId, amount, reason }) => {
    const response = await api.post("/api/paytabs/refund", {
      orderId,
      amount,
      reason,
    });
    return response;
  },
};
